/**
 * ErrorBoundary — cattura errori React non gestiti in un sotto-albero
 * e mostra una UI di fallback al posto della schermata bianca.
 *
 * Usato a livello route (dentro AppShell) così:
 *  - header e nav restano funzionanti
 *  - l'utente può navigare via menu o cliccare "Ricarica"
 *  - i logout/login non sono mai affetti (sono fuori dal Boundary)
 *
 * Logging:
 *  - console.error (sempre, anche in produzione)
 *  - Sentry NON è integrato per scelta — quando lo aggiungeremo, il
 *    posto è dentro `componentDidCatch`
 *
 * Per ripristinare dopo un crash basta cambiare route (resetKey).
 */

import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = {
  children: ReactNode
  /**
   * Quando questo valore cambia, lo state viene resettato — utile per
   * "uscire" dall'errore navigando ad un'altra route.
   */
  resetKey?: string | number
}

type State = {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] uncaught render error:', error, info.componentStack)
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null })
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6">
        <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/15 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="flex flex-col gap-1.5">
            <h2 className="text-lg font-bold text-foreground">
              Qualcosa è andato storto
            </h2>
            <p className="text-sm text-muted-foreground">
              Si è verificato un errore imprevisto. Riprova o torna alla home.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-2 max-h-32 overflow-auto rounded-md bg-muted p-2 text-left text-[11px] text-muted-foreground">
                {this.state.error.message}
              </pre>
            )}
          </div>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 rounded-lg bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground transition-colors hover:bg-secondary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <RefreshCw className="h-4 w-4" />
            Ricarica la pagina
          </button>
        </div>
      </div>
    )
  }
}
